import React, { useState } from 'react';
import styled from 'styled-components';
import { register } from '../../api/services/authService';
import BackgroundImage from '../../assets/log-bg.jpg';

// Styled Components
const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(7, 7, 7, 0.8);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
	backdrop-filter: blur(0.8px);
`;

const ModalContainer = styled.div`
	position: relative;
	background: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.9)),
		url(${BackgroundImage}) no-repeat center center;
	background-size: cover;
	padding: 3rem 2.5rem;
	border-radius: 20px;
	box-sizing: border-box;
	width: 420px;
	max-height: 90vh;
	overflow-y: auto;
	box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
	text-align: center;
	color: #ffffff;
	animation: fadeIn 0.5s ease-in-out;

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: translateY(-20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
`;

const ModalHeader = styled.h2`
	margin: 0 0 2rem;
	font-family: 'Montserrat', sans-serif;
	font-size: 1.8rem;
	color: #F4D03F;
	letter-spacing: 1.2px;
`;

const Label = styled.label`
	display: block;
	margin-bottom: 0.5rem;
	text-align: left;
	font-size: 1rem;
	color: #F8F9F9;
`;

const InputContainer = styled.div`
	position: relative;
	width: 100%;
`;

const Input = styled.input`
	color: #000;
	width: 100%;
	padding: 0.8rem 2.5rem 0.8rem 0.8rem;
	margin-bottom: 1rem;
	font-family: 'Poppins', sans-serif;
	box-sizing: border-box;
	border: none;
	border-radius: 10px;
	font-size: 1rem;
	transition: all 0.3s ease;

	&:focus {
		outline: none;
		border: 2px solid #F4D03F;
		box-shadow: 0 0 8px rgba(244, 208, 63, 0.7);
	}
`;

const Select = styled.select`
	color: #000;
	width: 100%;
	padding: 0.8rem;
	margin-bottom: 1rem;
	font-family: 'Poppins', sans-serif;
	box-sizing: border-box;
	border: none;
	border-radius: 10px;
	font-size: 1rem;
	cursor: pointer;
	transition: all 0.3s ease;

	&:focus {
		outline: none;
		border: 2px solid #F4D03F;
		box-shadow: 0 0 8px rgba(244, 208, 63, 0.7);
	}
`;

const ShowHideButton = styled.button`
	position: absolute;
	top: 35%;
	right: 10px;
	transform: translateY(-50%);
	background: transparent;
	border: none;
	cursor: pointer;
	font-size: 1.2rem;
	color: #555;
	transition: color 0.3s ease;

	&:hover {
		color: #F4D03F;
	}
`;

const SubmitButton = styled.button`
	background: linear-gradient(90deg, #F4D03F, #F39C12);
	color: #000;
	padding: 0.8rem 1.5rem;
	border: none;
	margin-top: 1rem;
	width: 100%;
	font-family: 'Montserrat', sans-serif;
	border-radius: 25px;
	font-weight: bold;
	cursor: pointer;
	font-size: 1rem;
	transition: all 0.3s ease;
	box-shadow: 0px 4px 15px rgba(244, 208, 63, 0.4);

	&:hover {
		background: linear-gradient(90deg, #F39C12, #F4D03F);
		transform: translateY(-2px);
		box-shadow: 0px 6px 20px rgba(243, 156, 18, 0.5);
	}
`;

const CloseButton = styled.button`
	position: absolute;
	top: 10px;
	right: 10px;
	background: transparent;
	color: #ffffff;
	border: none;
	cursor: pointer;
	font-size: 1.5rem;
	transition: color 0.3s ease;

	&:hover {
		color: #F4D03F;
	}
`;

const ErrorMessage = styled.p`
	color: #ff6b6b;
	background-color: rgba(255, 107, 107, 0.1);
	padding: 0.8rem;
	border-radius: 8px;
	margin-bottom: 1rem;
	font-size: 0.9rem;
`;

const LoginLinkContainer = styled.div`
	margin-top: 1.5rem;
	padding-top: 1.5rem;
	border-top: 1px solid rgba(255, 255, 255, 0.2);
	font-size: 0.95rem;
	color: #F8F9F9;
`;

const LoginLink = styled.span`
	color: #F4D03F;
	font-weight: bold;
	cursor: pointer;
	text-decoration: underline;
	transition: all 0.3s ease;

	&:hover {
		color: #F39C12;
		text-decoration: none;
	}
`;


const RegisterModal = ({ isOpen, onClose, onSwitchToLogin }) => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'Admin'
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');

    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword((prev) => !prev);
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        // Validation
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match!');
            return;
        }

        if (formData.password.length < 4) {
            setError('Password must be at least 4 characters!');
            return;
        }

        try {
            const result = await register(formData); // Call the register function from authService

            if (result.success) {
                alert(result.message || 'Registration successful! You can now login.');

                setFormData({
                    username: '',
                    email: '',
                    password: '',
                    confirmPassword: '',
                    role: 'farmer'
                });

                if (onSwitchToLogin) {
                    onSwitchToLogin();
                } else {
                    onClose();
                }
            } else {
                setError(result.message || 'Registration failed. Please try again.');
            }
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay>
            <ModalContainer>
                <CloseButton onClick={onClose}>&times;</CloseButton>
                <ModalHeader>Join M.A.K.A.S.A. Program</ModalHeader>
                {error && <ErrorMessage>{error}</ErrorMessage>}
                <form onSubmit={handleSubmit}>
                    <Label htmlFor="username">Username</Label>
                    <Input
                        type="text"
                        id="username"
                        name="username"
                        placeholder="Enter your username"
                        value={formData.username}
                        onChange={handleChange}
                        required
                    />

                    <Label htmlFor="email">Email</Label>
                    <Input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />

                    <Label htmlFor="password">Password</Label>
                    <InputContainer>
                        <Input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            placeholder="Enter your password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />
                        <ShowHideButton
                            type="button"
                            onClick={togglePasswordVisibility}
                        >
                            {showPassword ? '🙈' : '👁️'}
                        </ShowHideButton>
                    </InputContainer>

                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <InputContainer>
                        <Input
                            type={showConfirmPassword ? "text" : "password"}
                            id="confirmPassword"
                            name="confirmPassword"
                            placeholder="Confirm your password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                        />
                        <ShowHideButton
                            type="button"
                            onClick={toggleConfirmPasswordVisibility}
                        >
                            {showConfirmPassword ? '🙈' : '👁️'}
                        </ShowHideButton>
                    </InputContainer>

                    <Label htmlFor="role">Role</Label>
                    <Select
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                    >
                        <option value="farmer">Farmer</option>
                        <option value="researcher">Researcher</option>
                        <option value="admin">Admin</option>
                    </Select>

                    <SubmitButton type="submit">Register</SubmitButton>
                </form>

                <LoginLinkContainer>
                    Already have an account? <LoginLink onClick={onSwitchToLogin}>Login here</LoginLink>
                </LoginLinkContainer>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default RegisterModal;